import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ValidationRules, sanitize, validateField, checkPasswordStrength } from '../utils/validation'
import { isOnPremDeployment } from '../config/deploymentConfig'
import GitHubAccountSelectionModal from './GitHubAccountSelectionModal'
import CopyrightFooter from './CopyrightFooter'
import Seo from './Seo'
import './AuthForm.css'

const AuthForm = ({ mode = 'login', onNavigateHome }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const hasRedirectedRef = useRef(false)

  const isSignUp = mode === 'register'
  const [invitedEmail, setInvitedEmail] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [isResetPassword, setIsResetPassword] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, strength: '', feedback: [] })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showGitHubModal, setShowGitHubModal] = useState(false)
  const [authMessage, setAuthMessage] = useState('')

  const { user, loading: authLoading, signUp, signIn, signInWithOAuth, resetPassword, signOut } = useAuth()

  // Read invited email from URL parameter or sessionStorage after component mounts
  useEffect(() => {
    // Check URL parameter first (takes precedence)
    const params = new URLSearchParams(location.search)
    const emailParam = params.get('email')

    if (emailParam) {
      setEmail(emailParam)
      setInvitedEmail(emailParam)
      return
    }

    // Fall back to sessionStorage
    const invited = sessionStorage.getItem('invitedEmail') || ''
    if (invited) {
      setInvitedEmail(invited)
      setEmail(invited)
    }
  }, [location.search])

  // Check for authentication message from protected route redirect (runs once on mount)
  useEffect(() => {
    const message = sessionStorage.getItem('authMessage')
    if (message) {
      setAuthMessage(message)
      sessionStorage.removeItem('authMessage')
    }

    // Clear explicitLogout flag when arriving at signin page
    // This ensures subsequent unauthorized access attempts will save the URL
    sessionStorage.removeItem('explicitLogout')
  }, [])

  // Handle OAuth session completion - redirect authenticated users
  useEffect(() => {
    // If user just became authenticated (OAuth completed) and we're not loading
    if (user && !authLoading && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true

      // Check if this is a login after explicit logout
      const explicitLogout = sessionStorage.getItem('explicitLogout')
      if (explicitLogout) {
        // Clear flags - user explicitly logged out, don't redirect to last page
        sessionStorage.removeItem('explicitLogout')
        sessionStorage.removeItem('redirectAfterLogin')
        sessionStorage.removeItem('authMessage')
        navigate('/projects')
        return
      }

      // Check if there's a redirect path from protected route
      const redirectPath = sessionStorage.getItem('redirectAfterLogin')
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin')
        navigate(redirectPath)
      } else {
        // Fallback: navigate to projects page if user is authenticated but no saved redirect path
        // This handles the case where user signs in with OAuth directly from login page
        navigate('/projects')
      }
    }
  }, [user, authLoading, navigate])

  // Helper function to handle post-login redirect
  const handlePostLoginRedirect = () => {
    // Check if this is a login after explicit logout
    const explicitLogout = sessionStorage.getItem('explicitLogout')
    if (explicitLogout) {
      // Clear both flags - user explicitly logged out, don't redirect to last page
      sessionStorage.removeItem('explicitLogout')
      sessionStorage.removeItem('redirectAfterLogin')
      sessionStorage.removeItem('authMessage')
      navigate('/projects')
      return
    }

    // Check sessionStorage first (more reliable across re-renders)
    const redirectPath = sessionStorage.getItem('redirectAfterLogin')
    if (redirectPath) {
      sessionStorage.removeItem('redirectAfterLogin')
      // Don't clear invitedEmail yet - might be needed on invitation page
      navigate(redirectPath)
      return
    }

    // Fall back to location state
    const from = location.state?.from
    if (from) {
      navigate(from)
    } else {
      // Always redirect to /projects after successful login
      navigate('/projects')
    }
  }

  // Helper function to convert technical error messages to user-friendly ones
  const getUserFriendlyErrorMessage = (errorMessage) => {
    if (!errorMessage) return 'An unexpected error occurred'

    const lowerError = errorMessage.toLowerCase()

    // Rate limiting errors
    if (lowerError.includes('rate limit') || lowerError.includes('too many requests')) {
      return 'Too many attempts. Please wait a few minutes and try again.'
    }

    // Email-related errors
    if (lowerError.includes('email rate limit')) {
      return 'Too many signup attempts. Please try again in a few minutes.'
    }

    // Invalid credentials
    if (lowerError.includes('invalid login') ||
        lowerError.includes('invalid credentials') ||
        lowerError.includes('email not confirmed')) {
      return 'Invalid email or password. Please check your credentials.'
    }

    // Email not confirmed
    if (lowerError.includes('email not confirmed')) {
      return 'Please confirm your email address before signing in. Check your inbox for the confirmation link.'
    }

    // Invalid email format
    if (lowerError.includes('invalid email')) {
      return 'Please enter a valid email address.'
    }

    // Weak password
    if (lowerError.includes('password') &&
        (lowerError.includes('weak') || lowerError.includes('short') || lowerError.includes('strength'))) {
      return 'Password is too weak. Please use a stronger password with at least 8 characters.'
    }

    // Network errors
    if (lowerError.includes('network') || lowerError.includes('fetch')) {
      return 'Network error. Please check your connection and try again.'
    }

    // Default: return original message if no match
    return errorMessage
  }

  // Validation functions
  const validateEmail = (email) => {
    return validateField(email, ValidationRules.email)
  }

  const validatePassword = (password) => {
    if (isSignUp) {
      return validateField(password, ValidationRules.password)
    }
    return password ? null : 'Password is required'
  }

  const validateFullName = (name) => {
    if (!isSignUp) return null
    if (!name || name.trim().length === 0) {
      return 'Full name is required'
    }
    if (name.trim().length > 100) {
      return 'Full name cannot exceed 100 characters'
    }
    return null
  }

  // Handle field changes with validation and sanitization
  const handleEmailChange = (e) => {
    const rawEmail = e.target.value
    setEmail(rawEmail)

    const error = validateEmail(rawEmail)
    setFieldErrors(prev => ({ ...prev, email: error }))
  }

  const handlePasswordChange = (e) => {
    const rawPassword = e.target.value
    setPassword(rawPassword)
    
    const error = validatePassword(rawPassword)
    setFieldErrors(prev => ({ ...prev, password: error }))
    
    if (isSignUp) {
      const strength = checkPasswordStrength(rawPassword)
      setPasswordStrength(strength)
    }
  }

  const handleConfirmPasswordChange = (e) => {
    const confirmPass = e.target.value
    setConfirmPassword(confirmPass)
    
    const error = confirmPass !== password ? 'Passwords do not match' : null
    setFieldErrors(prev => ({ ...prev, confirmPassword: error }))
  }

  const handleFullNameChange = (e) => {
    const sanitizedName = sanitize.text(e.target.value)
    setFullName(sanitizedName)
    
    const error = validateFullName(sanitizedName)
    setFieldErrors(prev => ({ ...prev, fullName: error }))
  }

  // Form validation before submission
  const validateForm = () => {
    const errors = {}
    
    const emailError = validateEmail(email)
    if (emailError) errors.email = emailError
    
    // Only validate password if not in reset password mode
    if (!isResetPassword) {
      const passwordError = validatePassword(password)
      if (passwordError) errors.password = passwordError
    }
    
    if (isSignUp) {
      const nameError = validateFullName(fullName)
      if (nameError) errors.fullName = nameError
      
      if (password !== confirmPassword) {
        errors.confirmPassword = 'Passwords do not match'
      }
    }
    
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setMessage('')

    // Validate form
    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      // Sanitize inputs before API calls
      const sanitizedEmail = sanitize.email(email)
      const sanitizedFullName = sanitize.text(fullName)

      if (isResetPassword) {
        const { error } = await resetPassword(sanitizedEmail)
        if (error) {
          setError(getUserFriendlyErrorMessage(error.message))
        } else {
          setMessage('Password reset email sent! Check your inbox.')
        }
      } else if (isSignUp) {
        const { data, error } = await signUp(sanitizedEmail, password, {
          full_name: sanitizedFullName
        })

        if (error) {
          setError(getUserFriendlyErrorMessage(error.message))
        } else {
          // Check if a session was unexpectedly created (shouldn't happen with email confirmation enabled)
          if (data?.session) {
            // Sign out immediately - email confirmation should be required
            await signOut()
            setError('Unexpected error: Please contact support. Email confirmation is not properly configured.')
            return
          }

          // Check if there's a redirect path - if so, user can still use it after confirming email
          const redirectPath = sessionStorage.getItem('redirectAfterLogin')
          if (redirectPath) {
            setMessage('Check your email for the confirmation link! After confirming, you can return to complete your invitation.')
          } else {
            setMessage('Check your email for the confirmation link!')
          }
        }
      } else {
        const { error } = await signIn(sanitizedEmail, password)
        if (error) {
          setError(getUserFriendlyErrorMessage(error.message))
        } else {
          // Sign-in successful, redirect to the intended page or home
          handlePostLoginRedirect()
        }
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleOAuthSignIn = async (provider) => {
    // For GitHub, show the account selection modal first
    if (provider === 'github') {
      setShowGitHubModal(true)
      return
    }

    // For other providers, proceed directly
    setLoading(true)
    setError('')

    try {
      const { error } = await signInWithOAuth(provider)
      if (error) {
        setError(getUserFriendlyErrorMessage(error.message))
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleGitHubOAuthContinue = async () => {
    setLoading(true)
    setError('')

    try {
      const { error } = await signInWithOAuth('github')
      if (error) {
        setError(getUserFriendlyErrorMessage(error.message))
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    // Don't clear email if it's from an invitation
    if (!invitedEmail) {
      setEmail('')
    }
    setPassword('')
    setConfirmPassword('')
    setFullName('')
    setError('')
    setMessage('')
    setFieldErrors({})
    setPasswordStrength({ score: 0, strength: '', feedback: [] })
    setIsResetPassword(false)
    setShowPassword(false)
    setShowConfirmPassword(false)
  }

  if (isResetPassword) {
    return (
      <>
        <div className="auth-container">
          <div className="auth-card">
            <h2 className="auth-title">Reset Password</h2>
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  className={`form-input ${fieldErrors.email ? 'error' : ''}`}
                  placeholder="Enter your email"
                />
                {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
              </div>

              {error && <div className="error-message">{error}</div>}
              {message && <div className="success-message">{message}</div>}

              <button
                type="submit"
                className="auth-button primary"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <button
                type="button"
                className="auth-button secondary"
                onClick={() => {
                  setIsResetPassword(false)
                  resetForm()
                }}
              >
                Back to Sign In
              </button>
            </form>

            <div className="terms-notice">
              <p>
                By using our platform, you agree to our{' '}
                <Link to="/terms">Terms of Service</Link> and{' '}
                <Link to="/privacy">Privacy Policy</Link>.
              </p>
            </div>
          </div>
        </div>
        <CopyrightFooter />
      </>
    )
  }

  return (
    <>
      <Seo
        title={isSignUp ? 'Sign Up – MemBrowse' : 'Sign In – MemBrowse'}
        description={isSignUp
          ? 'Create a free MemBrowse account and start tracking firmware memory footprint in CI.'
          : 'Sign in to your MemBrowse account to view memory dashboards, budgets, and build comparisons.'}
        path={isSignUp ? '/signup' : '/signin'}
      />
      <GitHubAccountSelectionModal
        isOpen={showGitHubModal}
        onClose={() => setShowGitHubModal(false)}
        onContinue={handleGitHubOAuthContinue}
      />

      <div className="auth-container">
        <div className="auth-card">
          <h2 className="auth-title">
            {isSignUp ? 'Create Account' : 'Sign In'}
          </h2>

          {invitedEmail && (
            <div className="invitation-email-notice">
              <p>
                {isSignUp
                  ? "You've been invited to join a project. Create an account to view your invitation."
                  : "You've been invited to join a project. Sign in or create an account to view your invitation."}
              </p>
            </div>
          )}

          {authMessage && (
            <div className="auth-redirect-message">
              <p>{authMessage}</p>
            </div>
          )}

        <form onSubmit={handleSubmit} className="auth-form">
          {isSignUp && (
            <div className="form-group">
              <label htmlFor="fullName">Full Name</label>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={handleFullNameChange}
                required
                className={`form-input ${fieldErrors.fullName ? 'error' : ''}`}
                placeholder="Enter your full name"
                maxLength="100"
              />
              {fieldErrors.fullName && <div className="field-error">{fieldErrors.fullName}</div>}
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              required
              className={`form-input ${fieldErrors.email ? 'error' : ''}`}
              placeholder="Enter your email"
              maxLength="254"
            />
            {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={handlePasswordChange}
                required
                className={`form-input ${fieldErrors.password ? 'error' : ''}`}
                placeholder={isSignUp ? 'Create a strong password' : 'Enter your password'}
                maxLength="128"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {showPassword ? (
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C7 20 2.73 16.39 1 12A18.45 18.45 0 0 1 5.06 5.06L17.94 17.94ZM12 14A2 2 0 1 1 14 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  ) : (
                    <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  )}
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </button>
            </div>
            {fieldErrors.password && <div className="field-error">{fieldErrors.password}</div>}
            
            {isSignUp && password && (
              <div className="password-strength">
                <div className={`password-strength-bar strength-${passwordStrength.strength}`}>
                  <div 
                    className="password-strength-fill" 
                    style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                  ></div>
                </div>
                <div className="password-strength-text">
                  Password strength: <span className={`strength-${passwordStrength.strength}`}>
                    {passwordStrength.strength || 'weak'}
                  </span>
                </div>
                {passwordStrength.feedback.length > 0 && (
                  <ul className="password-feedback">
                    {passwordStrength.feedback.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          
          {isSignUp && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  required
                  className={`form-input ${fieldErrors.confirmPassword ? 'error' : ''}`}
                  placeholder="Confirm your password"
                  maxLength="128"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {showConfirmPassword ? (
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C7 20 2.73 16.39 1 12A18.45 18.45 0 0 1 5.06 5.06L17.94 17.94ZM12 14A2 2 0 1 1 14 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    )}
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>
              {fieldErrors.confirmPassword && <div className="field-error">{fieldErrors.confirmPassword}</div>}
            </div>
          )}
          
          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}
          
          <button 
            type="submit" 
            className="auth-button primary"
            disabled={loading}
          >
            {loading ? 'Processing...' : (isSignUp ? 'Sign Up' : 'Sign In')}
          </button>
        </form>

        {!isOnPremDeployment() && (
          <div className="auth-divider">
            <span>or</span>
          </div>
        )}

        {!isOnPremDeployment() && (
          <div className="oauth-buttons">
            <button
              type="button"
              className="oauth-button github"
              onClick={() => handleOAuthSignIn('github')}
              disabled={loading}
            >
              <svg viewBox="0 0 24 24" className="oauth-icon">
                <path fill="currentColor" d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              Continue with GitHub
            </button>

            <button
              type="button"
              className="oauth-button google"
              onClick={() => handleOAuthSignIn('google')}
              disabled={loading}
            >
              <svg viewBox="0 0 24 24" className="oauth-icon">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>
        )}

          <div className="terms-notice">
            <p>
              By {isSignUp ? 'signing up' : 'signing in'}, you agree to our{' '}
              <Link to="/terms">Terms of Service</Link> and{' '}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </div>

        {!isOnPremDeployment() && (
          <div className="auth-footer">
            {isSignUp ? (
              <p>
                Already have an account?{' '}
                <button
                  type="button"
                  className="auth-link"
                  onClick={() => navigate('/signin')}
                >
                  Sign In
                </button>
              </p>
            ) : (
              <div>
                <p>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => navigate('/signup')}
                  >
                    Sign Up
                  </button>
                </p>
                <p>
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => {
                      setIsResetPassword(true)
                      // Don't clear email if it's from an invitation
                      if (!invitedEmail) {
                        setEmail('')
                      }
                      setPassword('')
                      setError('')
                      setMessage('')
                      setFieldErrors({})
                    }}
                  >
                    Forgot your password?
                  </button>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    <CopyrightFooter />
    </>
  )
}

export default AuthForm 